import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import DatePicker from "../DatePicker";
import getAdminWinners, {
  AdminWinnerRow,
  weeklyDateByWeek,
} from "../../helpers/getAdminWinners";
import { MAIN_PRIZE_DATE } from "../../helpers/getWinner";
import {
  exportAdminWinnersCsv,
  exportAdminWinnersExcel,
} from "../../helpers/exportWinners";
import { IconTrophy } from "./AdminIcons";
import "../../Admin.scss";

type WinnerView = "daily" | "weekly" | "main";

const weekOptions = [1, 2, 3, 4, 5];

const yesterday = () => moment().subtract(1, "day");

const statusLabel = (status: string): string => {
  switch (status) {
    case "winner":
      return "dobitnik";
    case "first_backup":
      return "1.\u00A0rezerva";
    case "second_backup":
      return "2.\u00A0rezerva";
    case "third_backup":
      return "3.\u00A0rezerva";
    default:
      return status;
  }
};

const pillClass = (status: string): string => {
  if (status === "winner") return "is-winner";
  if (status.includes("backup")) return "is-backup";
  return "is-not_found";
};

const isWinner = (status: string): boolean => status === "winner";
const isBackup = (status: string): boolean => status.includes("backup");

const rowClass = (status: string): string => {
  if (isWinner(status)) return "is-winner-row";
  if (isBackup(status)) return "is-backup-row";
  return "";
};

const toExportRows = (rows: AdminWinnerRow[]) =>
  rows.map((row) => ({
    phone: row.identity,
    award: row.award,
    ranking: row.ranking,
    status: row.status,
    name: row.name,
    email: row.email,
    code: row.code,
    reserve_for: row.reserve_for,
  }));

const AdminWinners: React.FC = () => {
  const [view, setView] = useState<WinnerView>("daily");
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(yesterday);
  const [mainDate, setMainDate] = useState<moment.Moment>(() =>
    moment(MAIN_PRIZE_DATE, "YYYYMMDD")
  );
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [winners, setWinners] = useState<AdminWinnerRow[]>([]);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [weekMenuOpen, setWeekMenuOpen] = useState(false);
  const weekMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      let date = selectedDate.format("YYYYMMDD");
      if (view === "weekly") {
        date = weeklyDateByWeek(selectedWeek);
      } else if (view === "main") {
        date = mainDate.format("YYYYMMDD");
      }

      const response = await getAdminWinners(view, date);
      setWinners(response);
      setEmpty(!response.length);
      setLoading(false);
    };

    load();
  }, [view, selectedWeek, selectedDate, mainDate]);

  useEffect(() => {
    if (view !== "weekly") {
      setWeekMenuOpen(false);
    }
  }, [view]);

  useEffect(() => {
    if (!weekMenuOpen) return;

    const closeMenu = (event: MouseEvent) => {
      if (!weekMenuRef.current?.contains(event.target as Node)) {
        setWeekMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [weekMenuOpen]);

  const fileBase = () => {
    if (view === "weekly") return `nedeljni-dobitnici-nedelja-${selectedWeek}`;
    if (view === "main") return `glavni-dobitnik-${mainDate.format("YYYYMMDD")}`;
    return `dnevni-dobitnici-${selectedDate.format("YYYYMMDD")}`;
  };

  const emptyTitle =
    view === "main"
      ? "Nema glavnog dobitnika"
      : view === "weekly"
        ? "Nema nedeljnih dobitnika"
        : "Nema dnevnih dobitnika";

  const emptyHint =
    view === "main"
      ? `Za ${mainDate.format(
          "DD.MM.YYYY"
        )} još nema rezultata izvlačenja. Izaberi drugi datum ili sačekaj da se glavna nagrada izvuče.`
      : "Za odabrani prikaz nema rezultata. Probaj drugi datum ili nedelju.";

  return (
    <section className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>Dobitnici</h1>
          <p className="admin-lede">
            Nagradna igra Ultra NCP · 07.09. – 12.10.2026 · dnevni, nedeljni i
            glavni dobitnici sa rezervama
          </p>
        </div>
      </header>

      <div className="admin-card">
        <div className="admin-tabs">
          <button
            type="button"
            className={view === "daily" ? "is-active" : ""}
            onClick={() => setView("daily")}
          >
            Dnevni
          </button>
          <button
            type="button"
            className={view === "weekly" ? "is-active" : ""}
            onClick={() => setView("weekly")}
          >
            Nedeljni
          </button>
          <button
            type="button"
            className={view === "main" ? "is-active" : ""}
            onClick={() => setView("main")}
          >
            Glavna nagrada
          </button>
        </div>

        <div className="admin-toolbar">
          {view === "daily" && (
            <DatePicker
              initialDate={selectedDate}
              setDate={(date: moment.Moment) => setSelectedDate(date)}
            />
          )}
          {view === "main" && (
            <DatePicker
              initialDate={mainDate}
              setDate={(date: moment.Moment) => setMainDate(date)}
            />
          )}
          {view === "weekly" && (
            <div className="admin-week-dropdown" ref={weekMenuRef}>
              <button
                type="button"
                className="admin-select admin-week-trigger"
                aria-haspopup="listbox"
                aria-expanded={weekMenuOpen}
                onClick={() => setWeekMenuOpen((open) => !open)}
              >
                {selectedWeek}. nedelja
              </button>
              {weekMenuOpen && (
                <ul className="admin-week-list" role="listbox">
                  {weekOptions.map((week) => (
                    <li key={week}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={week === selectedWeek}
                        className={week === selectedWeek ? "is-selected" : ""}
                        onClick={() => {
                          setSelectedWeek(week);
                          setWeekMenuOpen(false);
                        }}
                      >
                        {week}. nedelja
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="admin-export-row">
            <button
              type="button"
              className="admin-btn"
              onClick={() =>
                winners.length &&
                exportAdminWinnersExcel(toExportRows(winners), `${fileBase()}.xls`)
              }
              disabled={!winners.length}
            >
              Export Excel
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-outline"
              onClick={() =>
                winners.length &&
                exportAdminWinnersCsv(toExportRows(winners), `${fileBase()}.csv`)
              }
              disabled={!winners.length}
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {loading && <p className="admin-muted">Učitavanje...</p>}

      {!loading && empty && (
        <div className={`admin-empty ${view === "main" ? "is-main" : ""}`}>
          <div className="admin-empty-icon">
            <IconTrophy />
          </div>
          <h2>{emptyTitle}</h2>
          <p>{emptyHint}</p>
        </div>
      )}

      {!loading && winners.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Rang</th>
                <th>Status</th>
                <th>Ime</th>
                <th>Telefon</th>
                <th>Email</th>
                <th>Kod</th>
                <th>Nagrada</th>
                <th>Rezerva za</th>
              </tr>
            </thead>
            <tbody>
              {winners.map((winner) => (
                <tr key={winner.id} className={rowClass(winner.status)}>
                  <td>{isWinner(winner.status) ? winner.ranking : ""}</td>
                  <td>
                    <span className={`admin-pill ${pillClass(winner.status)}`}>
                      {statusLabel(winner.status)}
                    </span>
                  </td>
                  <td>{winner.name || "—"}</td>
                  <td className={isWinner(winner.status) ? "is-winner-phone" : ""}>
                    {winner.identity}
                  </td>
                  <td>{winner.email || "—"}</td>
                  <td>{winner.code}</td>
                  <td>{winner.award}</td>
                  <td>{winner.reserve_for || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminWinners;
